import { useState, useCallback, useRef } from 'react'
import { useApp } from '@/store/AppContext'
import { reportError } from '@/lib/errorReporter'
import { getSupabase } from '@/hooks/useAuth'
import type { AskUserQuestion } from '@/types'
import {
  buildAutomationMemoryEntries,
  findRelevantAutomationProfiles,
  recordAutomationProfileRun,
} from '@/services/automationProfileService'
import { buildRuntimeInstruction } from '@/lib/workspace'
import type { MessageAttachment, MessageRole, WorkspaceSurface } from '@/types'
import { API_BASE } from '@/lib/config'
import { filterReadyConnectorIds } from '@/shared/connectors/availability'

const MAX_HISTORY_MESSAGES = 24
const MAX_HISTORY_CHARS = 24000

function trimHistoryForStreaming(history: Array<{ role: 'user' | 'assistant'; content: string; image_url?: string }>) {
  const picked: Array<{ role: 'user' | 'assistant'; content: string; image_url?: string }> = []
  let totalChars = 0

  for (let i = history.length - 1; i >= 0; i -= 1) {
    const item = history[i]
    const itemChars = item.content.length
    const nextCount = picked.length + 1
    const nextChars = totalChars + itemChars

    if (picked.length > 0 && (nextCount > MAX_HISTORY_MESSAGES || nextChars > MAX_HISTORY_CHARS)) break

    picked.push(item)
    totalChars = nextChars
  }

  return picked.reverse()
}

// Batch-interval for smooth streaming (ms)
const BATCH_INTERVAL = 16  // ~60fps — SmoothStreamingText handles the visual animation

function deriveModelProfile(model: string | null | undefined): 'lite' | 'pro' | 'max' | null {
  const normalized = (model ?? '').toLowerCase()
  if (normalized.includes('max')) return 'max'
  if (normalized.includes('pro')) return 'pro'
  if (normalized.includes('lite') || normalized === 'sine-1') return 'lite'
  return null
}

export function useChat() {
  const {
    activeConversation,
    activeConversationId,
    createConversation,
    addMessage,
    updateMessage,
    updateAgentMessage,
    updateConversationMeta,
    incrementConversationCreditsUsed,
    settings,
    updateSettings,
    activeProjectInstructions,
  } = useApp()
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // Track the last ask_user_input message id so we can mark it answered
  const askUserMsgIdRef = useRef<string | null>(null)

  const sendMessage = useCallback(async (
    userInput: string,
    planningMode: boolean = false,
    studioMode: boolean = false,
    assistantRole: MessageRole = 'assistant',
    runtimeSurface?: WorkspaceSurface,
    attachments?: MessageAttachment[],
    backendInputOverride?: string,
    imageEditParams?: {
      referenceImageUrl?: string
      aspectRatio?: string
      maskDataUrl?: string
    },
  ) => {
    // Fjern [PLANLEGGINGSMODUS]-prefiks fra meldingen før den sendes til API
    // (flagget er allerede fanget opp i planningMode-parameteren)
    const cleanInput = userInput.startsWith('[PLANLEGGINGSMODUS] ')
      ? userInput.slice('[PLANLEGGINGSMODUS] '.length)
      : userInput
    const effectiveSurface = runtimeSurface ?? (studioMode ? 'studio' : 'chat')
    const displayInput = cleanInput
    const backendInput = backendInputOverride ?? buildRuntimeInstruction(cleanInput, effectiveSurface)
    const isPlanningMode = planningMode || userInput.startsWith('[PLANLEGGINGSMODUS]')
    // Ensure we have a real active conversation object, not just a stale id.
    // If the id exists while the conversation list is still hydrating/missing,
    // addMessage would no-op and the UI would only show the stop button.
    let convId = activeConversationId
    if (!convId || !activeConversation || activeConversation.id !== convId) {
      convId = createConversation(settings.model, studioMode ? 'studio' : 'chat')
    }

    if (convId && studioMode && activeConversation?.appMode !== 'studio') {
      updateConversationMeta(convId, { appMode: 'studio', useAgentMode: false })
    } else if (convId && !studioMode && assistantRole === 'agent') {
      // Fix: set appMode to 'agent' so getConversationSurface() returns 'agent'
      // when the conversation is reloaded or the user switches conversations.
      updateConversationMeta(convId, { appMode: 'agent', useAgentMode: true })
    }

    // Add user message (vis ren tekst uten planleggingsmodus-prefiks)
    addMessage(convId, { role: 'user', content: displayInput, attachments })

    // Add empty assistant message (will be streamed into)
    const assistantMsgId = addMessage(convId, {
      role: assistantRole,
      content: '',
      isStreaming: true,
      isAgentMessage: assistantRole === 'agent',
      agentStatus: assistantRole === 'agent' ? 'stopped' : undefined,
      modelProfile: deriveModelProfile(settings.model) ?? undefined,
    })

    setIsStreaming(true)
    abortRef.current = new AbortController()

    // ── Batch-buffer for smooth streaming ──────────────────────────
    let batchTimer: ReturnType<typeof setTimeout> | null = null
    let accumulated = ''

    const flushToUI = (final: boolean) => {
      if (batchTimer) {
        clearTimeout(batchTimer)
        batchTimer = null
      }
      updateMessage(convId!, assistantMsgId, accumulated, !final)
    }

    const scheduleBatch = () => {
      if (batchTimer) return
      batchTimer = setTimeout(() => {
        batchTimer = null
        updateMessage(convId!, assistantMsgId, accumulated, true)
      }, BATCH_INTERVAL)
    }
    // ───────────────────────────────────────────────────────────────

    try {
      // Build message history from conversation.
      // Include agent messages mapped to 'assistant' so context is preserved
      // when switching between chat and agent modes.
      const conv = activeConversation
      const history = conv?.messages
        .filter(m => m.role === 'user' || m.role === 'assistant' || m.role === 'agent')
        .filter(m => (m.content && m.content.trim() !== '' || m.imageGeneration?.url) && !m.isStreaming)
        .map(m => ({
          role: (m.role === 'agent' ? 'assistant' : m.role) as 'user' | 'assistant',
          // For assistant messages with a generated image, use the prompt as content
          // and pass the image URL so the backend can inject it as a vision block
          content: m.imageGeneration?.url
            ? (m.imageGeneration.prompt || m.content || 'Generert bilde')
            : m.content,
          ...(m.imageGeneration?.url ? { image_url: m.imageGeneration.url } : {}),
        })) ?? []
      const trimmedHistory = trimHistoryForStreaming(history)

      // Add the new user message at the end
      const messages = [
        ...trimmedHistory,
        { role: 'user' as const, content: backendInput },
      ]

      // Hent brukerminne fra settings (lastet fra Supabase via useUserMemory)
      const userMemory: Array<{key: string, value: string}> = [
        ...(settings.userMemory ?? []).map(m => ({ key: m.key, value: m.value })),
        ...buildAutomationMemoryEntries(convId, settings, cleanInput),
      ]

      const matchedAutomationProfiles = findRelevantAutomationProfiles(cleanInput, convId, settings)
      for (const profile of matchedAutomationProfiles.slice(0, 3)) {
        const result = recordAutomationProfileRun(
          convId,
          settings,
          profile.id,
          'context_reused',
          `Gjenbrukte ${profile.systemName}-profilen som kontekst for: ${cleanInput.slice(0, 80)}`,
        )
        if (result.sharedProfiles) {
          updateSettings({ sharedAutomationProfiles: result.sharedProfiles })
        }
      }

      // Bygg aktive skills for system prompt injeksjon
      const activeSkills = (settings.skills ?? [])
        .filter(s => s.enabled)
        .map(s => ({ name: s.name, description: s.description, system_prompt: s.systemPrompt }))

      // Bygg tilkoblede apper
      const connectorCapabilities = settings.connectorCapabilities ?? {}
      const connectorIds = Object.keys(connectorCapabilities)
      const connectedApps = filterReadyConnectorIds(
        connectorIds,
        connectorCapabilities,
        settings.connectorStatuses ?? {},
        settings.connectorDisabled ?? {},
      )

      // Hent JWT-token fra Supabase session for autentisering
      let authHeader: Record<string, string> = {}
      try {
        const { data: { session } } = await getSupabase().auth.getSession()
        if (session?.access_token) {
          authHeader = { 'Authorization': `Bearer ${session.access_token}` }
        }
      } catch { /* no-op: unauthenticated request */ }

      const response = await fetch(`${API_BASE}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({
          messages,
          model: settings.model || 'sine-1',
          language: 'no',
          user_memory: userMemory,
          conversation_id: convId,
          active_skills: activeSkills,
          connected_apps: connectedApps,
          planning_mode: isPlanningMode,
          project_instructions: activeProjectInstructions || undefined,
          studio_mode: studioMode,
          dev_mode: settings.devMode ?? false,
          // Image editing parameters
          ...(imageEditParams?.referenceImageUrl && { reference_image_url: imageEditParams.referenceImageUrl }),
          ...(imageEditParams?.aspectRatio && { image_aspect_ratio: imageEditParams.aspectRatio }),
          ...(imageEditParams?.maskDataUrl && { image_mask_data_url: imageEditParams.maskDataUrl }),
        }),
        signal: abortRef.current.signal,
      })

      if (!response.ok) {
        const err = await response.text()
        if (
          response.status === 402 ||
          err.toLowerCase().includes('credit') ||
          err.toLowerCase().includes('kreditt')
        ) {
          window.dispatchEvent(new CustomEvent('chat-insufficient-credits'))
          updateMessage(convId!, assistantMsgId, '', false)
          return
        }
        throw new Error(`API-feil: ${response.status} – ${err}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) throw new Error('Ingen response body')

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (!data) continue

          try {
            const parsed = JSON.parse(data)
            if (parsed.type === 'token') {
              accumulated += parsed.content
              scheduleBatch()
              // Dev panel: dispatch token event (throttled — only every 20th token)
              if (settings.devMode && accumulated.length % 20 < (parsed.content?.length || 1)) {
                window.dispatchEvent(new CustomEvent('dev-debug-event', { detail: { type: 'dev_debug', phase: 'sse_event', data: { event_type: 'token', chars: parsed.content?.length || 0, total_chars: accumulated.length } } }))
              }
            } else if (parsed.type === 'ask_user_input') {
              // Claude wants to ask the user questions — update the assistant message
              // with the question card data instead of text content
              // Backend sends { type, tool, data: { questions: [...] } }
              const questions: AskUserQuestion[] = parsed.data?.questions ?? parsed.questions ?? []
              flushToUI(true) // finalize any partial text first
              updateAgentMessage(convId!, assistantMsgId, {
                content: '',
                isStreaming: false,
                askUserInput: { questions, answered: false },
              })
              askUserMsgIdRef.current = assistantMsgId
            } else if (['message_compose', 'recipe_display', 'visualize_widget', 'places_map', 'weather_card', 'sports_card', 'present_files', 'trigger_suggestion', 'interface_suggestion', 'automation_profile', 'suggest_agent_mode'].includes(parsed.type)) {
              // Frontend tool renderer — update the assistant message with tool data
              flushToUI(true)
              updateAgentMessage(convId!, assistantMsgId, {
                content: accumulated || '',
                isStreaming: false,
                toolData: {
                  type: parsed.type as 'message_compose' | 'recipe_display' | 'visualize_widget' | 'places_map' | 'weather_card' | 'sports_card' | 'present_files' | 'trigger_suggestion' | 'interface_suggestion' | 'automation_profile' | 'suggest_agent_mode',
                  data: parsed.data,
                },
              })
              accumulated = '' // reset so flushToUI at end doesn’t overwrite
            } else if (parsed.type === 'image_generating') {
              // Image generation started — show animated ImageGeneration component
              flushToUI(true)
              updateAgentMessage(convId!, assistantMsgId, {
                content: accumulated || '',
                isStreaming: true,
                imageGeneration: {
                  state: 'generating',
                  prompt: parsed.prompt || '',
                },
              })
            } else if (parsed.type === 'image_done') {
              // Image generation completed — reveal image
              flushToUI(true)
              updateAgentMessage(convId!, assistantMsgId, {
                content: accumulated || '',
                isStreaming: false,
                imageGeneration: {
                  state: 'completed',
                  url: parsed.url,
                  prompt: parsed.prompt || '',
                },
              })
              accumulated = '' // image is the result, no extra text needed
            } else if (parsed.type === 'image_failed') {
              // Image generation failed
              flushToUI(true)
              updateAgentMessage(convId!, assistantMsgId, {
                content: accumulated || '',
                isStreaming: false,
                imageGeneration: {
                  state: 'failed',
                  error: parsed.error || 'Ukjent feil',
                },
              })
              accumulated = ''
            } else if (parsed.type === 'dev_debug') {
              // Dev mode debug event — dispatch to DevPanel via custom event
              window.dispatchEvent(new CustomEvent('dev-debug-event', { detail: parsed }))
            } else if (parsed.type === 'done') {
              // Streaming ferdig
              window.dispatchEvent(new CustomEvent('dev-debug-event', { detail: { type: 'dev_debug', phase: 'stream_done', data: parsed } }))
              if (typeof parsed.credit_cost === 'number' && parsed.credit_cost > 0 && convId) {
                incrementConversationCreditsUsed(convId, parsed.credit_cost)
              }
              // Oppdater kreditter i UI
              if (typeof parsed.credits_remaining === 'number' && parsed.credits_remaining >= 0) {
                window.dispatchEvent(new CustomEvent('credits-updated', { detail: { credits: parsed.credits_remaining } }))
              } else {
                window.dispatchEvent(new CustomEvent('chat-credits-refresh'))
              }
            } else if (parsed.type === 'error') {
              window.dispatchEvent(new CustomEvent('dev-debug-event', { detail: { type: 'dev_debug', phase: 'stream_error', data: { message: parsed.message } } }))
              if (parsed.code === 'insufficient_credits') {
                window.dispatchEvent(new CustomEvent('chat-insufficient-credits'))
              }
              throw new Error(parsed.message)
            }
          } catch (_parseErr) {
            // ignore JSON parse errors for partial chunks
          }
        }
      }

      // Mark streaming as done – flush final content
      flushToUI(true)
    } catch (err: unknown) {
      if (batchTimer) clearTimeout(batchTimer)
      if (err instanceof Error && err.name === 'AbortError') {
        // User stopped generation – keep what we have
        updateMessage(convId!, assistantMsgId, accumulated || '', false)
      } else {
        // Classify the error for a user-friendly message
        let userMsg: string
        const errMsg = err instanceof Error ? err.message : 'Ukjent feil'
        if (!navigator.onLine) {
          userMsg = '*Ingen internettforbindelse. Sjekk nettverket og prøv igjen.*'
        } else if (err instanceof TypeError && errMsg.includes('fetch')) {
          userMsg = '*Kunne ikke nå serveren. Prøv igjen om litt.*'
        } else if (errMsg.includes('429')) {
          userMsg = '*For mange forespørsler. Vent litt og prøv igjen.*'
        } else if (errMsg.includes('401')) {
          userMsg = '*Ikke innlogget. Logg inn og prøv igjen.*'
        } else if (errMsg.includes('500')) {
          userMsg = '*Serverfeil. Vi jobber med saken — prøv igjen om litt.*'
        } else {
          userMsg = `*Feil: ${errMsg}*`
        }
        updateMessage(convId!, assistantMsgId, userMsg, false)
        // Log to GitHub Issues (only real errors, not user-initiated aborts)
        reportError({
          type: 'api',
          message: `useChat streaming feil: ${errMsg}`,
          stack: err instanceof Error ? err.stack : undefined,
          extra: { online: navigator.onLine },
        })
      }
    } finally {
      setIsStreaming(false)
      abortRef.current = null
    }
  }, [activeConversationId, activeConversation, createConversation, addMessage, updateMessage, updateAgentMessage, updateConversationMeta, incrementConversationCreditsUsed, settings, updateSettings, activeProjectInstructions])

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  // Called when the user submits answers to the ask_user_input_v0 card
  const handleAskUserSubmit = useCallback((answers: Record<number, string[]>, questions: AskUserQuestion[], studioMode: boolean = false) => {
    const convId = activeConversationId
    if (!convId) return
    // Mark the question card as answered
    if (askUserMsgIdRef.current) {
      updateAgentMessage(convId, askUserMsgIdRef.current, {
        askUserInput: { questions, answered: true },
      })
      askUserMsgIdRef.current = null
    }
    const isBuildConfirmation = questions.length === 1
      && /klar til å bygge|skal jeg bygge|ready to build|build this/i.test(questions[0]?.title ?? '')

    // Detect older/newer clarification questions about whether Sine should do the work or answer briefly.
    const isIntentClarification = questions.length === 1
      && /utføre en oppgave.*svare.*råd|dyp arbeidsflyt.*svar kort|deep workflow.*brief answer|agent-modus.*chat-modus/i.test(questions[0]?.title ?? '')

    const selectedConfirmationAnswers = answers[0] ?? []
    const selectedConfirmationText = selectedConfirmationAnswers.join(' ')

    // Handle intent clarification: route to deep work or short answer based on user's choice.
    if (isIntentClarification) {
      // Find the original user message that triggered the clarification.
      // Filter out empty messages and get the last real user input.
      const lastUserMsg = activeConversation?.messages
        ?.filter(m => m.role === 'user' && m.content?.trim())
        ?.slice(-1)[0]?.content ?? selectedConfirmationText

      if (/dyp arbeidsflyt|deep workflow|bruk verktøy|use tools|utfør oppgaven|agent-modus/i.test(selectedConfirmationText)) {
        // User wants Sine to do the work — dispatch an event that ChatView can pick up.
        window.dispatchEvent(new CustomEvent('intent-clarification-result', {
          detail: { mode: 'full_agent', originalMessage: lastUserMsg, studioMode },
        }))
        return
      } else {
        // User wants a short answer — re-send without forcing a full workflow.
        sendMessage(lastUserMsg || selectedConfirmationText, false, studioMode, 'agent', studioMode ? 'studio' : 'agent')
        return
      }
    }

    if (isBuildConfirmation && /bygg dette|build this|ja/i.test(selectedConfirmationText)) {
      sendMessage('Ja, bygg dette nå basert på planen over.', false, studioMode)
      return
    }

    if (isBuildConfirmation && /mer informasjon|add more information/i.test(selectedConfirmationText)) {
      sendMessage('Jeg vil legge til mer informasjon før du bygger.', false, studioMode)
      return
    }

    // Build the summary message: "Q: ... \nA: ..."
    const lines: string[] = []
    questions.forEach((q, i) => {
      const selected = answers[i] ?? []
      const answerText = selected.length > 0 ? selected.join(', ') : '[Ingen preferanse]'
      lines.push(`Q: ${q.title}\nA: ${answerText}`)
    })
    const summaryText = lines.join('\n\n')
    // Send as a user message and continue the conversation, preserving studio mode
    sendMessage(summaryText, false, studioMode)
  }, [activeConversationId, activeConversation, updateAgentMessage, sendMessage])

  return { sendMessage, stopStreaming, isStreaming, handleAskUserSubmit }
}
